import { CareServiceOrgansKey, ConditionTemplate, TextToStructuredObservation } from '@/api';
import {
  DEGENERATIVE_CHANGE_CONDITIONS,
  ENDOMETRIAL_THICKNESS_CONDITION,
  FINDING_CONDITION_TEMPLATE,
} from '@/shared/constant';
import { FindingsByOrgan } from '@/shared/model';
import { isNotEmpty, isUterusAppearanceAbsentOrPartial } from '@/shared/util';

import { findMissingFieldsInGroupedFindings } from '../shared/util';
import { EXCLUDED_PRIOR_SCAN_COMPARISON_CONDITIONS } from './organ.constant';
import { ValidateOrganParams, ValidateOrganResult } from './organ.model';

export function sortFindingsWithRemovedLast(
  findings: TextToStructuredObservation[],
  isAddendumEnabled = false,
) {
  if (!isAddendumEnabled) {
    return [...findings];
  }

  return [...findings].sort((a, b) => {
    const aRemoved = a.renderedSummary.addendumStatus === 'removed' ? 1 : 0;
    const bRemoved = b.renderedSummary.addendumStatus === 'removed' ? 1 : 0;

    return aRemoved - bRemoved;
  });
}

export function filterFindings(
  findingsObj: TextToStructuredObservation[],
  isAddendumEnabled = false,
) {
  return findingsObj.reduce(
    (acc, finding) => {
      if (
        finding.conditionTemplate.condition &&
        DEGENERATIVE_CHANGE_CONDITIONS.includes(finding.conditionTemplate.condition) &&
        (!isAddendumEnabled || finding.renderedSummary?.addendumStatus !== 'removed')
      ) {
        acc.groupedFindings.push(finding);
      } else {
        acc.findings.push(finding);
      }

      return acc;
    },
    { findings: [], groupedFindings: [] } as {
      findings: TextToStructuredObservation[];
      groupedFindings: TextToStructuredObservation[];
    },
  );
}

export function sortConditionsVertically(templates: ConditionTemplate[]) {
  const filteredConditions = templates
    .filter(
      ({ condition }) =>
        !DEGENERATIVE_CHANGE_CONDITIONS.includes(condition) &&
        condition !== FINDING_CONDITION_TEMPLATE.OTHER,
    )
    .sort((a, b) => a.condition.localeCompare(b.condition));

  const otherCondition = templates.find(
    ({ condition }) => condition === FINDING_CONDITION_TEMPLATE.OTHER,
  );
  const columnCount = 3;
  const totalConditions = filteredConditions.length;
  const rowCount = Math.ceil(totalConditions / columnCount);
  const moduloRowCount = totalConditions % columnCount;

  const updatedArray = filteredConditions.reduce(
    (result, condition, index) => {
      const row = index % rowCount;
      const col = Math.floor(index / rowCount);

      if (col < columnCount) {
        const newResult = [...result];

        newResult[row * columnCount + col] = condition;

        return newResult;
      }

      return result;
    },
    new Array(totalConditions + moduloRowCount),
  );

  const emptyIndex = updatedArray.findIndex((condition) => !condition);

  if (otherCondition) {
    updatedArray[emptyIndex > -1 ? emptyIndex : updatedArray.length] = otherCondition;
  }

  return updatedArray;
}

export function hasMissingRequiredFields(
  organ: CareServiceOrgansKey,
  findings: TextToStructuredObservation[],
): boolean {
  return findings
    .filter(
      (finding) =>
        finding?.conditionTemplate?.condition &&
        !finding.conditionTemplate.condition.startsWith('default_question_'),
    )
    .some(
      (finding) =>
        findMissingFieldsInGroupedFindings({ [organ]: [finding] } as FindingsByOrgan)[organ]?.error,
    );
}

interface MissingComparisonOptions {
  validateStatus?: boolean;
}

export function isFindingMissingPriorScanComparison(
  finding: TextToStructuredObservation,
  options: MissingComparisonOptions = {},
): boolean {
  const { validateStatus = true } = options;
  const condition = finding?.conditionTemplate?.condition;

  if (
    !condition ||
    condition.startsWith('default_question_') ||
    EXCLUDED_PRIOR_SCAN_COMPARISON_CONDITIONS.includes(condition)
  ) {
    return false;
  }

  return (
    finding.renderedSummary?.priorScanComparisonStatus !== 'not_applicable' &&
    (!finding.renderedSummary?.priorScanStudyId ||
      (validateStatus && !finding.renderedSummary?.priorScanComparisonStatus))
  );
}

export function hasMissingPriorScanComparison(
  findings: TextToStructuredObservation[],
  options?: MissingComparisonOptions,
): boolean {
  return findings.some((finding) => isFindingMissingPriorScanComparison(finding, options));
}

export function validateDefaultQuestions(
  organ: CareServiceOrgansKey,
  findings: TextToStructuredObservation[],
  defaultQuestionStatus: Record<CareServiceOrgansKey, Record<string, boolean>>,
): boolean {
  const organStatus = defaultQuestionStatus?.[organ];

  if (!organStatus) {
    return true;
  }

  return Object.entries(organStatus).every(([key, validStatus]) => {
    if (key === ENDOMETRIAL_THICKNESS_CONDITION && isUterusAppearanceAbsentOrPartial(findings)) {
      return true;
    }

    return !validStatus;
  });
}

export function validateOrgan({
  defaultQuestionStatus,
  findings,
  isAddendumEnabled = false,
  isOrganDisabled,
  organ,
  shouldValidatePriorScanComparison = false,
}: ValidateOrganParams): ValidateOrganResult {
  const activeFindings = isAddendumEnabled
    ? findings.filter((finding) => finding.renderedSummary?.addendumStatus !== 'removed')
    : findings;

  const hasFindings = isNotEmpty(activeFindings);

  // Disabled organs with no findings should not show validation errors
  const shouldIgnoreErrors = isOrganDisabled && !hasFindings;

  const missingRequired = hasMissingRequiredFields(organ, activeFindings);

  const missingPriorScanComparison =
    shouldValidatePriorScanComparison && hasMissingPriorScanComparison(activeFindings);

  const hasValidDefaultQuestions = validateDefaultQuestions(
    organ,
    activeFindings,
    defaultQuestionStatus,
  );

  const hasDefaultQuestionError = shouldIgnoreErrors ? false : !hasValidDefaultQuestions;

  const organHasError = missingRequired || hasDefaultQuestionError || missingPriorScanComparison;

  return {
    findingHasError: missingRequired || missingPriorScanComparison,
    organHasError,
  };
}

export const formatQuestionDisplayValue = (value: string) =>
  value.replace(/(\b[a-z](?!\s))/g, (x) => x.toUpperCase());
